"use client"

import * as React from "react"
import { useLocation, useParams } from "react-router-dom"

import { resolveBranchSlugFromUserBranchAccess } from "@/features/auth/lib"
import { buildAccessibleBranches } from "@/features/auth/lib/accessible-branches"
import {
  getActiveWorkspaceAccesses,
  isSingleBranchWorkspaceAccess,
  resolveWorkspaceContextBranchId,
} from "@/features/auth/lib/workspace-user-access"
import { useAuthContextStore } from "@/features/auth/services/auth-context-store"
import { type BranchSummary, useUserBranchAccessStore } from "@/features/auth/services/user-branch-access-store"
import { useActiveWorkspaceSlug } from "@/features/auth/hooks/use-active-workspace-slug"
import { useTenantAuthFlow } from "@/features/auth/providers/tenant-auth-flow-provider"
import { resolveWorkspaceAccessRecord } from "@/features/dashboard/model/church-nav"
import { extractBranchSlugFromAdminPath } from "@/shared/lib/branch/branch-admin-routing"
import { isReservedBranchSlug } from "@/shared/lib/routing/reserved-slugs"

function isNonEmptyString(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0
}

export function useUserBranchAccess() {
  const { pathname } = useLocation()
  const params = useParams<{ branchSlug?: string }>()
  const hydrated = useUserBranchAccessStore((state) => state.hydrated)
  const hydrate = useUserBranchAccessStore((state) => state.hydrate)
  const setBranchAccess = useUserBranchAccessStore((state) => state.setBranchAccess)
  const { authMe } = useTenantAuthFlow()
  const activeWorkspaceId = useAuthContextStore((state) => state.activeWorkspaceId)
  const activeWorkspaceSlug = useActiveWorkspaceSlug()

  React.useEffect(() => {
    if (!hydrated) {
      hydrate()
    }
  }, [hydrate, hydrated])

  const selectedWorkspaceAccess = React.useMemo(() => {
    if (!authMe) {
      return null
    }

    const activeWorkspaceAccess = getActiveWorkspaceAccesses(authMe)
    if (isNonEmptyString(activeWorkspaceSlug)) {
      const routeWorkspaceAccess = resolveWorkspaceAccessRecord(authMe, activeWorkspaceSlug)
      if (routeWorkspaceAccess) {
        return routeWorkspaceAccess
      }
    }

    if (isNonEmptyString(activeWorkspaceId)) {
      return (
        activeWorkspaceAccess.find((access) => access.workspace.id === activeWorkspaceId) ??
        null
      )
    }

    return activeWorkspaceAccess[0] ?? null
  }, [activeWorkspaceId, activeWorkspaceSlug, authMe])

  const derivedBranches = React.useMemo<BranchSummary[]>(
    () => buildAccessibleBranches(selectedWorkspaceAccess),
    [selectedWorkspaceAccess],
  )

  const urlBranchSlug = React.useMemo(() => {
    /*
     * The route param is NOT a branch slug just because it sits in the branch position.
     *
     * `/:workspaceSlug/settings/branches` is a legitimate ORGANIZATION url — "settings" is a reserved slug,
     * and `parseTenantLocation` resolves it to the parish. React Router does not know that: it binds
     * `branchSlug = "settings"`, and taking it at face value made the app believe it was inside a branch
     * called "settings". The switcher showed that word as the branch, and the sidebar lost every entry that
     * depends on knowing which branch you are in — which is what a refresh after the auth-failure redirect
     * landed on.
     *
     * So the param is used only when it is not reserved; otherwise the path is parsed properly, which
     * returns the parish token for exactly these urls.
     */
    const fromParams = params?.branchSlug
    if (isNonEmptyString(fromParams) && !isReservedBranchSlug(fromParams)) {
      return fromParams
    }
    const fromPath = extractBranchSlugFromAdminPath(pathname)
    return isNonEmptyString(fromPath) ? fromPath : null
  }, [params?.branchSlug, pathname])

  const derivedPrimary = React.useMemo<BranchSummary | null>(() => {
    if (!selectedWorkspaceAccess) {
      return null
    }

    if (derivedBranches.length === 0) {
      return null
    }

    if (urlBranchSlug) {
      const fromUrl = derivedBranches.find((branch) => branch.slug === urlBranchSlug)
      if (fromUrl) {
        return fromUrl
      }
    }

    const resolvedSlug = resolveBranchSlugFromUserBranchAccess(selectedWorkspaceAccess, null)
    /*
     * The resolved slug is the `parish` TOKEN for a workspace-level URL, and no branch is listed under it in a
     * mono workspace (its parish is invisible). So resolve the token the one canonical way instead of matching
     * it against slugs — this is where "which branch am I in" used to come out null for mono, leaving the whole
     * app to fall back on whatever each screen guessed next.
     */
    if (resolvedSlug && isSingleBranchWorkspaceAccess(selectedWorkspaceAccess)) {
      const contextBranchId = resolveWorkspaceContextBranchId(selectedWorkspaceAccess)
      return derivedBranches.find((branch) => branch.id === contextBranchId) ?? null
    }
    if (resolvedSlug) {
      return derivedBranches.find((branch) => branch.slug === resolvedSlug) ?? null
    }

    return null
  }, [derivedBranches, selectedWorkspaceAccess, urlBranchSlug])

  React.useEffect(() => {
    if (!hydrated) return
    if (!authMe) return

    setBranchAccess({
      primaryBranch: derivedPrimary,
      accessibleBranches: derivedBranches,
    })
  }, [authMe, derivedBranches, derivedPrimary, hydrated, setBranchAccess])

  const primaryBranch = React.useMemo(() => (authMe ? derivedPrimary : null), [authMe, derivedPrimary])
  const accessibleBranches = React.useMemo(() => (authMe ? derivedBranches : []), [authMe, derivedBranches])

  const accessibleBranchSlugs = React.useMemo(
    () => accessibleBranches.map((branch) => branch.slug).filter(Boolean),
    [accessibleBranches],
  )

  const canAccessBranch = React.useCallback(
    (slug: string) => accessibleBranchSlugs.includes(slug),
    [accessibleBranchSlugs],
  )

  return {
    hydrated,
    primaryBranch,
    primaryBranchSlug: primaryBranch?.slug ?? "",
    accessibleBranches,
    accessibleBranchSlugs,
    isSingleBranchWorkspace: Boolean(
      selectedWorkspaceAccess && isSingleBranchWorkspaceAccess(selectedWorkspaceAccess),
    ),
    canAccessBranch,
  }
}
